import { logAmplitudeEvent } from 'amplitude/amplitude'
import { styled, Typography } from 'opensolar-ui'
import { MAX_DISPLAYED_CATEGORIES, useShopCategoryConfig } from 'pages/ordering/categoryConfig'
import { useTranslate } from 'ra-core'
import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useFeatureFlag } from 'util/featureFlags'

const Section = styled('div')({
  padding: '24px 0',
})

const SectionTitle = styled(Typography)({
  marginBottom: 0,
})

const SectionSubtitle = styled(Typography)({
  marginTop: 4,
  marginBottom: 24,
})

const RowsContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
})

const GAP = 16

const CategoryCard = styled(Link)({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: '20px 0 0 20px',
  backgroundColor: '#FFFFFF',
  borderRadius: 8,
  borderLeft: '6px solid #4272DD',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)',
  textDecoration: 'none',
  color: 'inherit',
  transition: 'box-shadow 0.2s ease, transform 0.2s ease',
  minHeight: 140,
  overflow: 'hidden',
  boxSizing: 'border-box',
  position: 'relative',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08)',
    transform: 'translateY(-1px)',
  },
})

const CategoryInfo = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  paddingBottom: 20,
  zIndex: 1,
})

const CategoryName = styled(Typography)({
  fontWeight: 600,
  fontSize: 18,
  color: 'black',
})

const ShopNowLink = styled(Typography)({
  color: '#1751D0',
  fontSize: 14,
  fontWeight: 600,
})

const CategoryImage = styled('img')<{ $variant?: 'default' | 'others' | 'inverter' | 'ev-charger' | 'mounting' }>(({
  $variant = 'default',
}) => {
  const baseStyles = {
    position: 'absolute' as const,
    right: 0,
    objectFit: 'contain' as const,
    objectPosition: 'bottom right',
  }

  if ($variant === 'inverter') {
    return {
      ...baseStyles,
      bottom: -40,
      right: 15,
      height: 175,
      width: 195,
    }
  }

  if ($variant === 'ev-charger') {
    return {
      ...baseStyles,
      bottom: -15,
      right: 15,
      height: 165,
      width: 185,
    }
  }

  if ($variant === 'mounting') {
    return {
      ...baseStyles,
      bottom: -5,
      right: 35,
      height: '100%',
      maxHeight: 140,
      width: 'auto',
      maxWidth: '55%',
    }
  }

  if ($variant === 'others') {
    return {
      ...baseStyles,
      bottom: -10,
      right: 15,
      height: 165,
      width: 185,
    }
  }

  return {
    ...baseStyles,
    bottom: 0,
    right: 16,
    height: '100%',
    maxHeight: 140,
    width: 'auto',
    maxWidth: '55%',
  }
})

const CATEGORY_IMAGES: Record<string, string> = {
  module: '/images/shop-category-panels.png',
  panels: '/images/shop-category-panels.png',
  modules: '/images/shop-category-panels.png',
  inverter: '/images/shop-category-inverter.png',
  inverters: '/images/shop-category-inverter.png',
  battery: '/images/shop-category-battery.png',
  batteries: '/images/shop-category-battery.png',
  storage: '/images/shop-category-battery.png',
  mounting: '/images/shop-category-mounting.png',
  'ev-charging': '/images/shop-category-ev-charger.png',
  'ev-chargers': '/images/shop-category-ev-charger.png',
  ev_charger: '/images/shop-category-ev-charger.png',
  other: '/images/shop-category-others.png',
  others: '/images/shop-category-others.png',
}

const getCategoryImage = (
  componentType: string,
  searchKey: string,
  subtypes?: (string | '*')[],
  customImageUrl?: string | null
): string => {
  if (customImageUrl) {
    return customImageUrl
  }

  if (CATEGORY_IMAGES[searchKey]) {
    return CATEGORY_IMAGES[searchKey]
  }

  if (subtypes && subtypes.length > 0 && subtypes[0] !== '*') {
    const firstSubtype = subtypes[0]
    if (CATEGORY_IMAGES[firstSubtype]) {
      return CATEGORY_IMAGES[firstSubtype]
    }
  }

  if (CATEGORY_IMAGES[componentType]) {
    return CATEGORY_IMAGES[componentType]
  }

  return CATEGORY_IMAGES.other
}

const getRowLayout = (categoryCount: number): number[] => {
  switch (categoryCount) {
    case 2:
      return [2]
    case 3:
      return [3]
    case 4:
      return [2, 2]
    case 5:
      return [3, 2]
    case 6:
      return [3, 3]
    case 7:
      return [4, 3]
    case 8:
      return [4, 4]
    default:
      return [categoryCount]
  }
}

interface ShopByCategoryProps {
  distributor?: string
}

const ShopByCategory = ({ distributor }: ShopByCategoryProps) => {
  const translate = useTranslate()
  const isShopUpliftEnabled = useFeatureFlag('shop_uplift', 'on')
  const { enabledCategories, isEmptyConfig } = useShopCategoryConfig()

  const visibleCategories = useMemo(
    () => enabledCategories.slice(0, MAX_DISPLAYED_CATEGORIES),
    [enabledCategories]
  )
  const categoryCount = visibleCategories.length
  const rowLayout = useMemo(() => getRowLayout(categoryCount), [categoryCount])

  const handleCategoryClick = (categoryName: string, searchKey: string) => {
    logAmplitudeEvent('hardware_category_block_clicked', {
      category: categoryName,
      searchKey,
      source: 'shop_homepage_category_blocks',
    })
  }

  if (!isShopUpliftEnabled || isEmptyConfig || categoryCount < 2) {
    return null
  }

  const rows: (typeof visibleCategories)[] = []
  let index = 0
  for (const colCount of rowLayout) {
    rows.push(visibleCategories.slice(index, index + colCount))
    index += colCount
  }

  return (
    <Section>
      <SectionTitle textVariant="h5Bold">{translate('Shop by Category')}</SectionTitle>
      <SectionSubtitle textVariant="h6">
        {translate(
          'Superior selection, better deals. Discover the best in the business with our industry-leading partners.'
        )}
      </SectionSubtitle>
      <RowsContainer>
        {rows.map((rowCategories, rowIndex) => (
          <div
            key={rowIndex}
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${rowCategories.length}, 1fr)`,
              gap: GAP,
            }}
          >
            {rowCategories.map((category) => {
              const link = distributor
                ? `/shop/search/${category.searchKey}/supplier/${distributor}`
                : `/shop/search/${category.searchKey}`
              const imageUrl = getCategoryImage(
                category.componentType,
                category.searchKey,
                category.subtypes,
                category.imageUrl
              )

              return (
                <CategoryCard
                  key={category.id}
                  to={link}
                  onClick={() => handleCategoryClick(category.name, category.searchKey)}
                >
                  <CategoryInfo>
                    <CategoryName>{category.name}</CategoryName>
                    <ShopNowLink>{translate('Shop now')}</ShopNowLink>
                  </CategoryInfo>
                  <CategoryImage
                    src={imageUrl}
                    alt={category.name}
                    $variant={
                      imageUrl.includes('shop-category-inverter')
                        ? 'inverter'
                        : imageUrl.includes('shop-category-ev-charger')
                          ? 'ev-charger'
                          : imageUrl.includes('shop-category-mounting')
                            ? 'mounting'
                            : imageUrl.includes('shop-category-others')
                              ? 'others'
                              : 'default'
                    }
                    onError={(e) => {
                      ;(e.target as HTMLImageElement).style.display = 'none'
                    }}
                  />
                </CategoryCard>
              )
            })}
          </div>
        ))}
      </RowsContainer>
    </Section>
  )
}

export default ShopByCategory
